import getSymbolFromCurrency from "currency-symbol-map";
import React, { useMemo } from "react";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import {
  IgButton,
  IgDivider,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { ensureNumber } from "@intelligems/ig-utils";
import {
  Grid,
  InputAdornment,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useGetCogsData } from "../../../api/cogs/get-cogs-data";
import { useUploadCogsData } from "../../../api/cogs/upload-cogs-data";
import { usePatchConfigurations } from "../../../api/configurations/patch-configurations";
import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useGetOrg } from "../../../api/organizations/get-organization";
import { processesKeys } from "../../../api/queryKeys";
import { useLoggedInUser } from "../../../api/user-auths/login";
import { UploadFile } from "../../../utils/UploadFile";

export const ManualManagement = () => {
  const user = useLoggedInUser();
  const organization = useGetOrg();
  const shippingCostConfig = useConfiguration("shipping_cost_per_order");
  const transactionFeePctConfig = useConfiguration("transaction_fee_pct");
  const mutateConfigurations = usePatchConfigurations();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const [inputShippingCostValue, setInputShippingCostValue] = useImmer<string>(
    shippingCostConfig?.value
  );
  const [inputTransactionFeePct, setInputTransactionFeePct] = useImmer<string>(
    (parseFloat(transactionFeePctConfig!.value) * 100).toString()
  );

  const fetchCogs = useGetCogsData();
  const cogsDataUpload = useUploadCogsData();
  const [isLoading, setIsLoading] = useImmer<boolean>(false);

  const onMutateConfigs = () => {
    const updatedConfigInputs: any[] = [];
    if (shippingCostConfig?.value !== ensureNumber(inputShippingCostValue)) {
      updatedConfigInputs.push({
        ...shippingCostConfig,
        value: ensureNumber(inputShippingCostValue),
      });
    }
    if (
      transactionFeePctConfig?.value !==
      ensureNumber(inputTransactionFeePct) / 100
    ) {
      updatedConfigInputs.push({
        ...transactionFeePctConfig,
        value: ensureNumber(inputTransactionFeePct) / 100,
      });
    }
    if (updatedConfigInputs.length > 0) {
      mutateConfigurations.mutate(updatedConfigInputs);
    }
  };

  const onFileUploadSuccess = (bucket: string, key: string) => {
    cogsDataUpload.mutate(
      {
        bucket,
        key,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
        },
        onError: () => {
          setIsLoading(false);
        },
      }
    );
  };

  const onCogsTemplateClick = () => {
    if (user.isSuccess) {
      fetchCogs.mutate({
        sync: false,
        email: user.data.user.email,
      });
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 1000);
      queryClient.setQueryData(processesKeys.one(organization.data.org.id), {
        processes: [
          {
            id: v4(),
            orgId: organization.data.org.id,
            type: "requestCogs",
            severity: "info",
            title: "Building COGS Template...",
            isLoading: true,
          },
        ],
      });
    }
  };
  const steps = useMemo(() => {
    return [
      {
        label: "Download COGS Template",
        content: (
          <StepContent>
            <Grid container item direction={"column"} rowSpacing={3}>
              <Grid item>
                <IgButton
                  variant={"contained"}
                  loading={fetchCogs.isPending}
                  onClick={onCogsTemplateClick}
                >
                  Download Template
                </IgButton>
              </Grid>
              <Grid item>
                <IgTypography
                  variant={"body2"}
                  color={theme.palette.text.secondary}
                >
                  Note: Any COGS already in Shopify will be pre-populated in
                  this template.
                </IgTypography>
              </Grid>
            </Grid>
          </StepContent>
        ),
      },
      {
        label: "Upload Completed COGS Template (Excel Only)",
        content: (
          <StepContent>
            <UploadFile
              variant={"contained"}
              color={"primary"}
              renderKey={"cogs"}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              organization={organization.data.org}
              onFileUploadSuccess={onFileUploadSuccess}
            >
              Upload COGS Excel File
            </UploadFile>
          </StepContent>
        ),
      },
    ];
  }, [
    isLoading,
    fetchCogs.isPending,
    inputTransactionFeePct,
    inputShippingCostValue,
    mutateConfigurations.isPending,
    transactionFeePctConfig?.value,
    shippingCostConfig?.value,
  ]);

  return (
    <Grid container item direction={"column"} rowSpacing={3}>
      <Grid item>
        <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
          Manual Management
        </IgTypography>
      </Grid>
      <Stepper
        sx={{
          "& .MuiStepIcon-root.Mui-active": {
            color: theme.igBackgroundColors.accentSolid,
          },
        }}
        orientation="vertical"
      >
        {steps.map((step, index) => (
          <Step key={step.label} active={true}>
            <StepLabel sx={{ fontWeight: 600 }}>
              <IgTypography>{step.label}</IgTypography>
            </StepLabel>
            {step.content}
          </Step>
        ))}
      </Stepper>
      <Grid item>
        <IgDivider spacing={"xs"} />
      </Grid>
      <Grid item>
        <IgTypography variant="h3" fontWeight={"fontWeightMedium"}>
          Define Additional Costs (Optional)
        </IgTypography>
      </Grid>
      <Grid container item direction={"column"} rowSpacing={4}>
        <Grid item>
          <IgTextField
            sx={{ minWidth: "300px" }}
            label={"Average Shipping / Fulfillment Cost per Order"}
            disabled={mutateConfigurations.isPending}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {getSymbolFromCurrency(
                    storeCurrencyConfig?.value?.code ?? "USD"
                  )}
                </InputAdornment>
              ),
            }}
            type={"number"}
            value={inputShippingCostValue}
            onChange={(e) => {
              setInputShippingCostValue(e.target.value);
            }}
            variant={"outlined"}
          />
        </Grid>
        <Grid item>
          <IgTextField
            label={"Transaction Fee Percent"}
            disabled={mutateConfigurations.isPending}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            type={"number"}
            value={inputTransactionFeePct}
            onChange={(e) => {
              setInputTransactionFeePct(e.target.value);
            }}
            variant={"outlined"}
          />
        </Grid>
        <Grid item>
          <IgButton
            variant={"contained"}
            loading={mutateConfigurations.isPending}
            disabled={
              (transactionFeePctConfig?.value ===
                ensureNumber(inputTransactionFeePct) / 100 &&
                shippingCostConfig?.value ===
                  ensureNumber(inputShippingCostValue)) ||
              inputTransactionFeePct.length === 0 ||
              inputShippingCostValue.length === 0
            }
            onClick={onMutateConfigs}
          >
            Save
          </IgButton>
        </Grid>
      </Grid>
    </Grid>
  );
};
